import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  Divider,
} from "@mui/material";
import {
  Dashboard,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  List as ListIcon,
  Landscape as LandscapeIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import { BarChart as AnalyticsIcon, Campaign as MarketingIcon } from "@mui/icons-material";

import { useNavigate } from "react-router-dom";
import logo from "./logo.png";
import { Email as EmailIcon } from "@mui/icons-material";

export default function SkylineLayout() {
  const [open, setOpen] = useState(false);
  const [clientsOpen, setClientsOpen] = useState(false);
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  const navigate = useNavigate();
  const toggleDrawer = () => setOpen(!open);

  const handleLogout = () => {
    window.localStorage.removeItem("vdcLogin");
    window.localStorage.removeItem("savedPassword");
    window.localStorage.removeItem("savedEmail");
    navigate("/");
  };

  return (
    <Box sx={{ display: "flex", marginBottom:"2rem" }}>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton onClick={toggleDrawer} edge="start" sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>

          <Avatar
            src={logo}
            alt="Logo"
            sx={{ width: "4rem", height: "4rem", mr: 1, marginLeft: "-1.6rem" }}
            variant="square"
          />

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1 }}
            style={{ fontWeight: "600", marginLeft: "-1rem" }}
          >
            Skyline Properties
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: open ? 240 : 70,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? 240 : 0,
            transition: "width 0.3s",
            overflowX: "hidden",
            boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
            borderRight: "none",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          },
        }}
      >
        <Box style={{ marginTop: "4rem" }}>
          <List>
            {/* Dashboard */}
            <Tooltip title={!open ? "Dashboard" : ""} placement="right">
              <ListItem button onClick={() => navigate("/dashboard")} disabled>
                <ListItemIcon>
                  <Dashboard sx={{ color: "#1976d2" }} />
                </ListItemIcon>
                {open && <ListItemText primary="Dashboard" />}
              </ListItem>
            </Tooltip>

            {/* Clients */}
            <Tooltip title={!open ? "Clients" : ""} placement="right">
              <ListItem button onClick={() => setClientsOpen(!clientsOpen)}>
                <ListItemIcon>
                  <PersonIcon sx={{ color: "#43a047" }} />
                </ListItemIcon>
                {open && <ListItemText primary="Clients" />}
                {open && (clientsOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItem>
            </Tooltip>
            <Collapse in={clientsOpen && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem
                  button
                  sx={{ pl: 4 }}
                  onClick={() => {
                    navigate("/list-clients");
                    toggleDrawer();
                  }}
                >
                  <ListItemIcon>
                    <ListIcon sx={{ color: "#0288d1" }} />
                  </ListItemIcon>
                  <ListItemText primary="List Clients" />
                </ListItem>
                <ListItem
                  button
                  sx={{ pl: 4 }}
                  onClick={() => {
                    navigate("/add-clients");
                    toggleDrawer();
                  }}
                >
                  <ListItemIcon>
                    <PersonAddIcon sx={{ color: "#7b1fa2" }} />
                  </ListItemIcon>
                  <ListItemText primary="Add Clients" />
                </ListItem>
              </List>
            </Collapse>

            {/* Properties */}
            <Tooltip title={!open ? "Properties" : ""} placement="right">
              <ListItem button onClick={() => setPropertiesOpen(!propertiesOpen)}>
                <ListItemIcon>
                  <LandscapeIcon sx={{ color: "#ef6c00" }} />
                </ListItemIcon>
                {open && <ListItemText primary="Properties" />}
                {open && (propertiesOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItem>
            </Tooltip>
            <Collapse in={propertiesOpen && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem
                  button
                  sx={{ pl: 4 }}
                  onClick={() => {
                    navigate("/list-properties");
                    toggleDrawer();
                  }}
                >
                  <ListItemIcon>
                    <ListIcon sx={{ color: "#0288d1" }} />
                  </ListItemIcon>
                  <ListItemText primary="List Properties" />
                </ListItem>
                <ListItem
                  button
                  sx={{ pl: 4 }}
                  onClick={() => {
                    navigate("/add-properties");
                    toggleDrawer();
                  }}
                >
                  <ListItemIcon>
                    <AddCircleOutlineIcon sx={{ color: "#2e7d32" }} />
                  </ListItemIcon>
                  <ListItemText primary="Add Properties" />
                </ListItem>
                <ListItem
                  button
                  sx={{ pl: 4 }}
                  onClick={() => {
                    navigate("/deleted-properties");
                    toggleDrawer();
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon sx={{ color: "red" }} />
                  </ListItemIcon>
                  <ListItemText primary="Deleted Properties" />
                </ListItem>
              </List>
            </Collapse>

            {/* Marketing */}
            <Tooltip title={!open ? "Marketing" : ""} placement="right">
              <ListItem button onClick={() => setMarketingOpen(!marketingOpen)}>
                <ListItemIcon>
                  <MarketingIcon sx={{ color: "#6a1b9a" }} />
                </ListItemIcon>
                {open && <ListItemText primary="Marketing" />}
                {open && (marketingOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItem>
            </Tooltip>
            <Collapse in={marketingOpen && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem
                  button
                  sx={{ pl: 4 }}
                  onClick={() => {
                    navigate("/email-marketing-clients");
                    toggleDrawer();
                  }}
                >
                  <ListItemIcon>
                    <EmailIcon sx={{ color: "#0288d1" }} />
                  </ListItemIcon>
                  <ListItemText primary="Email Marketing" />
                </ListItem>
              </List>
            </Collapse>

            {/* Analytics */}
            <Tooltip title={!open ? "Analytics" : ""} placement="right">
              <ListItem button onClick={() => setAnalyticsOpen(!analyticsOpen)}>
                <ListItemIcon>
                  <AnalyticsIcon sx={{ color: "#0288d1" }} />
                </ListItemIcon>
                {open && <ListItemText primary="Visitor Analytics" />}
                {open && (analyticsOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItem>
            </Tooltip>
            <Collapse in={analyticsOpen && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem
                  button
                  sx={{ pl: 4 }}
                  onClick={() => {
                    navigate("/visitor-count");
                    toggleDrawer();
                  }}
                >
                  <ListItemIcon>
                    <AnalyticsIcon sx={{ color: "#0288d1" }} />
                  </ListItemIcon>
                  <ListItemText primary="Visitor Count" />
                </ListItem>
              </List>
            </Collapse>
          </List>
        </Box>

        {/* Settings + Logout */}
        <Box>
          <Divider />
          <Tooltip disabled title={!open ? "Settings" : ""} placement="right">
            <ListItem button onClick={() => navigate("/settings")}>
              <ListItemIcon>
                <SettingsIcon sx={{ color: "#0288d1" }} />
              </ListItemIcon>
              {open && <ListItemText primary="Settings" />}
            </ListItem>
          </Tooltip>
          <Tooltip title={!open ? "Logout" : ""} placement="right">
            <ListItem button onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon sx={{ color: "red" }} />
              </ListItemIcon>
              {open && <ListItemText primary="Logout" sx={{ color: "red" }} />}
            </ListItem>
          </Tooltip>
        </Box>
      </Drawer>
    </Box>
  );
}
